// chat-persist-helpers.js — pure helpers for the per-turn transcript persist.
// The server owns the conversation transcript: the client appends its own turn,
// keyed by the server-stamped assistant message id, instead of overwriting the
// whole document — so concurrent tabs converge instead of clobbering each other.
// No DOM; unit-tested in chat-persist-helpers.test.mjs.

/**
 * The wire shape of one chat message: public fields only (private underscore
 * state like _streaming never leaves the tab). `prompt` — the full dispatched
 * backend prompt — rides along only when it differs from the display text
 * (attachment sends), so the server-side safety net can recognize its own copy
 * of the question, and regenerate keeps the exact prompt across reloads.
 */
export function serializeMessage(m) {
  const out = { id: m.id, role: m.role, content: m.content, timestamp: m.timestamp };
  if (m.prompt && m.prompt !== m.content) out.prompt = m.prompt;
  if (m.parts?.length) out.parts = m.parts;
  return out;
}

/**
 * The just-completed turn to append: the assistant message plus the user
 * message directly before it, serialized. Empty when the assistant message
 * isn't part of the log (nothing safe to persist).
 */
export function completedTurnMessages(messages, assistantMsg) {
  if (!Array.isArray(messages)) return [];
  const idx = messages.indexOf(assistantMsg);
  if (idx < 0) return [];
  const turn = [];
  const prev = messages[idx - 1];
  if (prev && prev.role === "user") turn.push(prev);
  turn.push(assistantMsg);
  return turn.map(serializeMessage);
}

/**
 * The pending (just-sent, unanswered) turn to append: the trailing user
 * message, if that's what the log currently ends with.
 */
export function pendingTurnMessages(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const last = messages[messages.length - 1];
  return last && last.role === "user" ? [serializeMessage(last)] : [];
}
